/* eslint-disable camelcase */
import { DateFormats, formatDate } from '@strata/helper-functions';
import { DateInputFormik } from '@strata/react-date-time';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import * as Yup from 'yup';

import { nullOnEmpty } from 'services/Helpers';

export const loadDirections = (directions) =>
    directions
        ? Array.from(directions).map((direction, i) => ({
              key: i,
              option: direction.option,
              value: Number(direction.value),
              dose_modifier: direction.dose_modifier,
              strength: Number(direction.strength),
              strength_uom: direction.strength_uom,
          }))
        : [];

const cleanNumber = (value) => value || 0;
const handleFalsy = (value, defaultValue) => value || defaultValue;

export const loadProductConfigs = (oid, values) => ({
    oid: handleFalsy(values.oid, oid),
    hcpcs_billing_unit: cleanNumber(values.hcpcs_billing_unit),
    hcpcs_billing_label: handleFalsy(values.hcpcs_billing_label, ''),
    hcpcs_package_size: cleanNumber(values.hcpcs_package_size),
    ndc_billing_unit: cleanNumber(values.ndc_billing_unit),
    ndc_billing_label: handleFalsy(values.ndc_billing_label, ''),
    ndc_package_size: cleanNumber(values.ndc_package_size),
    integration_gpi: values.integration_gpi,
    integration_gpis: values.integration_gpis ? Array.from(values.integration_gpis).join(', ') : '',
    multistrength_flag: handleFalsy(values.multistrength_flag, false),
    therapeutic_category: handleFalsy(values.therapeutic_category, ''),
    fmh_specialty_list: handleFalsy(values.fmh_specialty_list, ''),
    biologic_type: handleFalsy(values.biologic_type, ''),
    is_ldd: handleFalsy(values.is_ldd, false),
    send_ldd_task: false,
    send_ldd_task_confirm: '',
    hcpcs_conversion_rate: cleanNumber(values.hcpcs_conversion_rate),
    product_savings_multiplier: cleanNumber(values.product_savings_multiplier),
    directions: loadDirections(values.direction_options),
    is_brand_default: handleFalsy(values.is_brand_default, false),
    drug_quantity_default: values.drug_quantity_default,
    days_supply_default: values.days_supply_default,
    additional_med_options: values.additional_med_options
        ? Array.from(values.additional_med_options).join('\n')
        : '',
    claim_lookback_days: values.claim_lookback_days,
});

const transformGpisToArray = (gpiString) => gpiString.split(',').map((gpi) => gpi.trim());

export const productConfigSchema = (enableGpiIntegrationList) =>
    Yup.object({
        oid: Yup.string()
            .max(50, validationStrings.maxCharacter[50])
            .required(validationStrings.requiredField.errorMessage),
        hcpcs_billing_unit: Yup.number().required(validationStrings.requiredField.errorMessage),
        hcpcs_billing_label: Yup.string()
            .max(20, 'Must be 20 characters or less.')
            .required(validationStrings.requiredField.errorMessage),
        hcpcs_package_size: Yup.number().required(validationStrings.requiredField.errorMessage),
        hcpcs_conversion_rate: Yup.number().required(validationStrings.requiredField.errorMessage),
        product_savings_multiplier: Yup.number().required(
            validationStrings.requiredField.errorMessage,
        ),
        ndc_billing_unit: Yup.number().required(validationStrings.requiredField.errorMessage),
        ndc_billing_label: Yup.string()
            .max(20, 'Must be 20 characters or less.')
            .required(validationStrings.requiredField.errorMessage),
        ndc_package_size: Yup.number().required(validationStrings.requiredField.errorMessage),
        multistrength_flag: Yup.bool(),
        therapeutic_category: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            .max(50, validationStrings.maxCharacter[50]),
        fmh_specialty_list: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            .max(50, validationStrings.maxCharacter[50]),
        biologic_type: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            .max(50, validationStrings.maxCharacter[50]),
        is_ldd: Yup.bool(),
        send_ldd_task: Yup.bool(),
        send_ldd_task_confirm: Yup.string().when('send_ldd_task', {
            is: true,
            then: (schema) =>
                schema
                    .required(validationStrings.requiredField.errorMessage)
                    .matches(/^CONFIRM$/, validationStrings.requiredField.errorMessage),
        }),
        is_brand_default: Yup.bool(),
        drug_quantity_default: Yup.number().required(validationStrings.requiredField.errorMessage),
        days_supply_default: Yup.number().required(validationStrings.requiredField.errorMessage),
        needs_by_date_days: Yup.number().nullable(),
        directions: Yup.array()
            .of(
                Yup.object({
                    option: Yup.string()
                        .max(200, 'Must be 200 characters or less.')
                        .required(validationStrings.requiredField.errorMessage),
                    value: Yup.number()
                        .positive('Value must be positive')
                        .required(validationStrings.requiredField.errorMessage),
                    dose_modifier: Yup.string(),
                    strength: Yup.number()
                        .positive('Strength must be positive')
                        .required(validationStrings.requiredField.errorMessage),
                    strength_uom: Yup.string().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                }),
            )
            .required('Must have directions'),
        claim_lookback_days: Yup.number().required(validationStrings.requiredField.errorMessage),
        ...(enableGpiIntegrationList && {
            integration_gpis: Yup.array()
                .transform((_, originalValue) => transformGpisToArray(originalValue))
                .of(Yup.string().length(14, 'Must be 14 characters\n'))
                .required(validationStrings.requiredField.errorMessage),
        }),
        ...(!enableGpiIntegrationList && {
            integration_gpi: Yup.string()
                .length(14, 'Must be 14 characters.')
                .required(validationStrings.requiredField.errorMessage),
        }),
    });

export const packageProductConfigs = (values, enableGpiIntegrationList) => {
    const options = values.additional_med_options.split('\n');
    return {
        oid: values.oid,
        hcpcs_billing_unit: values.hcpcs_billing_unit,
        hcpcs_billing_label: values.hcpcs_billing_label,
        hcpcs_package_size: values.hcpcs_package_size,
        hcpcs_conversion_rate: values.hcpcs_conversion_rate,
        product_savings_multiplier: values.product_savings_multiplier,
        ndc_billing_unit: values.ndc_billing_unit,
        ndc_billing_label: values.ndc_billing_label,
        ndc_package_size: values.ndc_package_size,
        integration_gpi: values.integration_gpi,
        integration_gpis: enableGpiIntegrationList
            ? transformGpisToArray(values.integration_gpis)
            : [values.integration_gpi],
        multistrength_flag: values.multistrength_flag,
        direction_options: values.directions,
        additional_med_options: nullOnEmpty(options),
        therapeutic_category: values.therapeutic_category,
        fmh_specialty_list: values.fmh_specialty_list,
        biologic_type: values.biologic_type,
        is_ldd: values.is_ldd,
        send_ldd_task: values.is_ldd ? (values.send_ldd_task ?? false) : false,
        is_brand_default: values.is_brand_default,
        drug_quantity_default: values.drug_quantity_default,
        days_supply_default: values.days_supply_default,
        claim_lookback_days: values.claim_lookback_days,
    };
};

export const loadPayerValues = ({ oid, segment_id, config }) => ({
    oid: config.oid ?? oid,
    segment_id: config.segment_id ?? segment_id,
    valid_sites_of_care: config.valid_sites_of_care || [],
    preferred_billing_type: config.preferred_billing_type ?? '',
    is_active: config.is_active ?? true,
    start_date: config.start_date ?? dayjs().startOf('day').format(DateFormats.HYPHEN_DATE),
    end_date: config.end_date ?? null,
    use_drug_qty_defaults: config.use_drug_qty_defaults,
    is_brand_default: config.is_brand_default ?? null,
    prior_auth_required: config.prior_auth_required ?? true,
    last_update: formatDate(config.last_update, DateFormats.DATE_TIME),
});

export const payerValuesSchema = Yup.object({
    oid: Yup.string()
        .max(50, validationStrings.maxCharacter[50])
        .required(validationStrings.requiredField.errorMessage),
    segment_id: Yup.string()
        .max(50, validationStrings.maxCharacter[50])
        .required(validationStrings.requiredField.errorMessage),
    preferred_billing_type: Yup.string().required(validationStrings.requiredField.errorMessage),
    is_active: Yup.bool().required(validationStrings.requiredField.errorMessage),
    start_date: DateInputFormik.validator().required(validationStrings.requiredField.errorMessage),
    end_date: DateInputFormik.validator()
        .nullable()
        .notRequired()
        .when('start_date', {
            is: (start_date) => !start_date,
            then: (schema) =>
                schema.test(
                    'end-date-requires-start',
                    'End date can only be set when start date is provided',
                    (value) => value == null,
                ),
            otherwise: (schema) =>
                schema.min(Yup.ref('start_date'), 'End date cannot be before start date'),
        }),
    use_drug_qty_defaults: Yup.bool().required(validationStrings.requiredField.errorMessage),
    is_brand_default: Yup.bool().required(validationStrings.requiredField.errorMessage).nullable(),
    prior_auth_required: Yup.bool().required(validationStrings.requiredField.errorMessage),
});

export const packagePayerValues = (values) => ({
    oid: values.oid,
    segment_id: values.segment_id,
    valid_sites_of_care: values.valid_sites_of_care,
    preferred_billing_type: values.preferred_billing_type,
    is_active: values.is_active,
    start_date: values.start_date,
    end_date: values.end_date,
    use_drug_qty_defaults: values.use_drug_qty_defaults,
    is_brand_default: values.is_brand_default,
    prior_auth_required: values.prior_auth_required,
});
